import { Controller } from 'stimulus'
import Axios from 'axios'

/**
 * Manages js for the submit step (StimulusJS controller)
 *
 * @class SubmitStepController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'underwritingStatus',
    'portalStatus',
  ]

  axios = Axios.create({
    headers: { 'Accept': 'application/json' }
  })

  /**
   * @function Controllers.SubmitStepController.initialize
   * @memberof Controllers
   * @description Initializes the controller with some needed variables.
   *
   * @return {undefined} manipulates DOM
   *
   */
  initialize() {
    this.underwritingStatus = null
    this.portalStatus       = null
    this.underwriteEvent    = 'ByteSyncCompletedAUSEvent'
    this.portalEvent        = 'IvyProcessedPortalAccountEvent'
  }

  /**
   * @function Controllers.SubmitStepController.connect
   * @memberof Controllers
   * @description Gets the aggregateId we'll be searching the events with and
   * kicks the whole thing off.
   *
   * @return {undefined} manipulates DOM
   *
   */
  connect() {
    this.aggregateId = this.data.get('aggregate-id')
    this.beginEventProcessing()
  }

  /**
   * @function Controllers.SubmitStepController.beginEventProcessing
   * @memberof Controllers
   * @description Reaches out to the Event Store for the last event available,
   * checks it for the events we're concerned with and kicks off polling for
   * more events. Error triggers _displayTYP.
   *
   * @return {undefined} manipulates DOM
   *
   */
  beginEventProcessing() {
    this.axios.get('/v1/event_store/events', {
      params: {
        before: 'head',
        limit:  1
      }
    }).then(function(response) {
        this.startTime = Number(new Date())
        this._searchEvents(response.data)
        this.timerInterval = setInterval(() => {
          this._pollForEvents()
        }, 3000)
      }.bind(this))
      .catch(function(error) {
        this._displayTYP()
      }.bind(this))
  }

  /**
   * @function Controllers.SubmitStepController._searchEvents
   * @memberof Controllers
   * @description Searches events for the underwrite and portal events first,
   * then checks to see if they have the aggregateId we're interested in. If we
   * have both of the variables filled, we trigger _displayTYP.
   * @private
   *
   * @param {Object} events - the events returned from the call to the Event
   * Store.
   *
   * @return {undefined} manipulates DOM
   *
   */
  _searchEvents(events) {
    if (events.length > 0) {
      this.lastEventNumber = events[events.length - 1].eventNumber
    } else {
      return
    }

    events.forEach(event => {
      if (event.eventType === this.underwriteEvent) {
        this._processUnderwriteEvent(event)
      } else if (event.eventType === this.portalEvent) {
        this._processPortalEvent(event)
      }
    }, this)

    this._maybePurchaseEventsFound()
    this._maybeRefinanceEventsFound()
  }

  /**
   * @function Controllers.SubmitStepController._processUnderwriteEvent
   * @memberof Controllers
   * @description Decides if we've found an event we're looking for and sets a
   * top level variable and a hidden field if so.
   * @private
   *
   * @param {Object} event - event from the event stream
   *
   * @return {undefined} manipulates DOM
   *
   */
  _processUnderwriteEvent(event) {
    const underwriteEventData = JSON.parse(event.data)

    if (underwriteEventData.AggregateId !== this.aggregateId) { return }

    this.underwritingStatus = underwriteEventData.Data.Recommendation
    this.underwritingStatusTarget.value = underwriteEventData.Data.Recommendation
  }

  /**
   * @function Controllers.SubmitStepController._processPortalEvent
   * @memberof Controllers
   * @description Decides if we've found an event we're looking for and sets a
   * top level variable and a hidden field if so.
   * @private
   *
   * @param {Object} event - event from the event stream
   *
   * @return {undefined} manipulates DOM
   *
   */
  _processPortalEvent(event) {
    const portalEventData = JSON.parse(event.data)

    if (portalEventData.AggregateId !== this.aggregateId) { return }

    if (portalEventData.Data.StatusCode == 201) {
      this.portalStatus = true
      this.portalStatusTarget.value = true
    } else {
      this._displayTYP()
    }
  }

  /**
   * @function Controllers.SubmitStepController._maybePurchaseEventsFound
   * @memberof Controllers
   * @description Checks the form-type and then the two variables that get
   * filled with event responses and if they're both present, triggers
   * _displayTYP.
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _maybePurchaseEventsFound() {
    if (this.data.get('form-type') !== 'Purchase') { return }
    if (this.underwritingStatus && this.portalStatus) { this._displayTYP() }
  }

  /**
   * @function Controllers.SubmitStepController._maybeRefinanceEventsFound
   * @memberof Controllers
   * @description Checks the form-type and then for the portal event and, if
   * present, triggers _displayTYP.
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _maybeRefinanceEventsFound() {
    if (this.data.get('form-type') !== 'Refinance') { return }
    if (this.portalStatus) { this._displayTYP() }
  }

  /**
   * @function Controllers.SubmitStepController._pollForEvents
   * @memberof Controllers
   * @description Checks to see if there is time left (the clock is ticking) and
   * continues to retrieve events from the Event Store until time is up.
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _pollForEvents() {
    if (this._timesUp()) {
      this._displayTYP()
    }

    this._readEventsFrom(this.lastEventNumber + 1)
  }

  /**
   * @function Controllers.SubmitStepController._timesUp
   * @memberof Controllers
   * @description Checks against a start time to determine time elapsed. If the
   * time elapsed is greater than the set time, it returns true.
   * @private
   *
   * @return {Boolean} true if time is up, false otherwise
   *
   */
  _timesUp() {
    return (Number(new Date()) - this.startTime) > 120000
  }

  /**
   * @function Controllers.SubmitStepController._readEventsFrom
   * @memberof Controllers
   * @description Retrieves events from Event Store from a location start to the
   * end at that point in time. Error triggers _displayTYP
   * @private
   *
   * @param {Integer} start - Position in the Event Store line-up.
   *
   * @return {undefined} manipulates DOM
   *
   */
  _readEventsFrom(start) {
    this.axios.get('/v1/event_store/events', {
      params: {
        after:        start,
        type:         `${this.underwriteEvent}||${this.portalEvent}`,
        aggregate_id: this.aggregateId
      }
    }).then(function(response) {
        this._searchEvents(response.data)
      }.bind(this))
      .catch(function(error) {
        this._displayTYP()
      }.bind(this))
  }

  /**
   * @function Controllers.SubmitStepController._displayTYP
   * @memberof Controllers
   * @description The ending funnel through which the fortune of the step flows.
   * Cancels polling and triggers full-app:next.
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _displayTYP() {
    this._cancelPoll()
    this.element.dispatchEvent(new CustomEvent('full-app:next'))
  }

  /**
   * @function Controllers.SubmitStepController._cancelPoll
   * @memberof Controllers
   * @description Clears the interval that was the mechanism of polling.
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _cancelPoll() {
    clearInterval(this.timerInterval)
  }
}
